import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { formatDate } from '@angular/common';
import { combineLatest, Observable } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { Pageable } from '../../model/rest/base';
import {
  productTypeLabel,
  subscriptionEventBadgeClass,
  subscriptionEventLabel,
  Subscription,
  SubscriptionEvent,
  SubscriptionProductType,
} from '../../model/v3/subscriptions';
import { SubscriptionsV3RestService } from '../../services/rest/v3/subscriptions-rest.service';
import { StudentV3RestService } from '../../services/rest/v3/student-rest.service';
import { TeacherV3RestService } from '../../services/rest/v3/teacher-rest.service';
import { SubscriptionRuleModalComponent } from '../../components/subscription-rule-modal/subscription-rule-modal.component';

interface SubscriptionEntry {
  subscription: Subscription;
  events: SubscriptionEvent[];
  eventsLoading: boolean;
}

/**
 * The "Subscriptions" tab of one member in the manager area: every
 * subscription of the student/teacher with its renewal rule (editable through
 * the shared modal) and the full event history underneath - the same data the
 * admin sees on the school subscriptions tab.
 */
@Component({
  selector: 'app-manager-member-subscriptions-page',
  templateUrl: './manager-member-subscriptions-page.component.html',
  standalone: false,
})
export class ManagerMemberSubscriptionsPageComponent implements OnInit {
  schoolId: number;
  loading = false;
  entries: SubscriptionEntry[] = [];
  /**
   * The same person may be both a student and a teacher in the school; each
   * tab shows only the products of its member context.
   */
  private allowedProducts: SubscriptionProductType[] = [];

  @ViewChild('ruleModal', { static: true })
  ruleModal: SubscriptionRuleModalComponent;

  constructor(
    private route: ActivatedRoute,
    private subscriptionsRest: SubscriptionsV3RestService,
    private studentRest: StudentV3RestService,
    private teacherRest: TeacherV3RestService,
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.route.parent.parent.paramMap,
      this.route.parent.paramMap,
    ]).pipe(
      tap(([schoolParams]) => (this.schoolId = Number(schoolParams.get('schoolId')))),
      switchMap(([, memberParams]) => this.resolvePersonId(memberParams.get('studentId'), memberParams.get('teacherId'))),
      switchMap((personId) => this.loadSubscriptions(personId)),
    ).subscribe();
  }

  private resolvePersonId(studentId: string | null, teacherId: string | null): Observable<number> {
    if (studentId != null) {
      this.allowedProducts = [SubscriptionProductType.StudentRegistration];
      return this.studentRest
        .getStudent(this.schoolId, Number(studentId))
        .pipe(map((student) => student.person.id));
    }
    this.allowedProducts = [
      SubscriptionProductType.TeacherRegistration,
      SubscriptionProductType.TeacherSubscription,
    ];
    return this.teacherRest
      .getTeacher(this.schoolId, Number(teacherId))
      .pipe(map((teacher) => teacher.person.id));
  }

  private loadSubscriptions(personId: number) {
    this.loading = true;
    this.entries = [];
    return this.subscriptionsRest
      .listSubscriptions(this.schoolId, {
        personId,
        pageable: Pageable.of(0, 10, ['id,ASC']),
      })
      .pipe(
        tap((page) => {
          this.entries = page.content
            .filter((subscription) => this.allowedProducts.includes(subscription.productType))
            .map((subscription) => ({
              subscription,
              events: [],
              eventsLoading: true,
            }));
          this.entries.forEach((entry) => this.loadEvents(entry));
        }),
        finalize(() => (this.loading = false)),
      );
  }

  private loadEvents(entry: SubscriptionEntry) {
    entry.eventsLoading = true;
    this.subscriptionsRest
      .getSubscriptionEvents(this.schoolId, entry.subscription.id)
      .pipe(finalize(() => (entry.eventsLoading = false)))
      .subscribe((events) => (entry.events = events));
  }

  productLabel(subscription: Subscription): string {
    return productTypeLabel(subscription.productType);
  }

  renewalLabel(subscription: Subscription): string {
    if (subscription.renewalStopped) {
      return 'renewals stopped';
    }
    if (!subscription.renewal.autoRenew) {
      return 'auto-renew off';
    }
    if (subscription.renewal.renewalMonthsLimit != null) {
      return `renews for ${subscription.renewal.renewalMonthsLimit} months`;
    }
    if (subscription.renewal.renewUntilDate) {
      return `renews until ${formatDate(subscription.renewal.renewUntilDate, 'd/MM/yyyy', 'en')}`;
    }
    return 'renews until stopped';
  }

  eventBadgeClass(event: SubscriptionEvent): string {
    return subscriptionEventBadgeClass(event.eventType);
  }

  eventLabel(event: SubscriptionEvent): string {
    return subscriptionEventLabel(event);
  }

  editRule(entry: SubscriptionEntry) {
    this.ruleModal.open(entry.subscription);
  }

  onRuleSaved(updated: Subscription) {
    const entry = this.entries.find((row) => row.subscription.id === updated.id);
    if (!entry) {
      return;
    }
    entry.subscription = updated;
    // every applied change writes an UPDATE event, so refresh the history
    this.loadEvents(entry);
  }
}
